<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, reactive } from 'vue'
import {
  api,
  type CalendarStatus,
  type BalanceSheetData,
  type DailyPnlData,
  type EnrichedSynthNavData,
  type GoldHoldingsLatestResponse,
  type GgeSynthNavLive,
  type BgaJhSynthNavLive,
} from '../api/client'
import PositionBreakdown from './PositionBreakdown.vue'
import PnlPerformanceMini from './PnlPerformanceMini.vue'
import { useFundEnablement } from '../composables/useFundEnablement'
import type { PerformanceFund } from '../api/client'
import { usePortfolio } from '../composables/usePortfolio'
import { allocatedFunds } from './strategy/allocationModel'
import { deriveHoldingsHybrid } from '../utils/holdingsHybrid'
import { computeGoldTodaysMovePct } from '../lib/goldHoldingsRows'
import { overlayPosition, provisionalLines as buildProvisionalLines, portfolioValueAdjustment } from '../lib/provisionalOverlay'
import { FUND_REGISTRY, REGISTERED_PORTFOLIOS, GATED_FUNDS, isLiveFundAccessibleFor, fundForPortfolio } from './nav/fundConfig'
import type { PotFund } from './nav/fundConfig'

// Fix-wave review (F6, final-review.md): this Fund/FUNDS pair is
// DELIBERATELY not the 5-fund registry (nav/fundConfig.ts's ALL_FUNDS) —
// OTP is excluded from the P&L rollup on purpose, not an oversight. OTP
// has no NAV/fund share class (it's a residual-cash overlay, not an OEIC
// holding), so it has no per-fund balance-sheet/daily-pnl series for this
// panel's hero Σ + per-fund breakdown to source from. OTP's own P&L hero
// lives in the OTP window (OtpWindowPanel.vue) via otpSummary, sourced
// from hedge_trades directly. If OTP ever needs to join this rollup, that
// is a deliberate design decision (see final-review.md F6) — not a
// silently-missed widening the way BGA/JH/GOLD/GGE were added here.
// JAPAN joins in plan 2026-09-29 Task 15, gated by isFundVisible (entitled
// holders only — fail-closed while /api/auth/me loads).
// CHARTERIS joins in Charteris Phase 3 (spec 2026-10-05 §7) the same way —
// gated by hasAccess/isFundVisible, so a non-entitled user never fetches or
// renders a Charteris row. AMATI joins in its Phase 3 (2026-10-13) the same way.
type Fund = PotFund
const FUNDS: readonly Fund[] = ['BGA', 'JH', 'GOLD', 'GGE', 'JAPAN', 'CHARTERIS', 'AMATI', 'MINERS', 'JAPANESG', 'TECH', 'ELECTRUM', 'JHTECH', 'ASIATECH', 'BARINGSOIL', 'SPWORLD', 'SMALLCAP']

// primaryAssetType is the ACTIVELY-HELD asset for the fund. BGA has two
// share classes — B Acc has always been zero units for active users, so
// we resolve to B Inc (BGA_INC, SEDOL 0606185) for the per-fund position
// lookup and P&L math. JH, GOLD, GGE, JAPAN, CHARTERIS (SEDOL BYQ2JZ5) and AMATI
// (SEDOL BMD8NV6) are single-class families. The gated funds read their pence NAV level from
// /api/japan/nav-latest, which serves any gated fund the caller can view.
const FUND_CONFIG: Record<Fund, { primaryAssetType: string; navUrl: string }> = {
  BGA:   { primaryAssetType: 'BGA_INC', navUrl: '/api/nav/bga'  },
  JH:    { primaryAssetType: 'JHGTL',   navUrl: '/api/nav/jh'   },
  GOLD:  { primaryAssetType: 'GOLD',    navUrl: '/api/nav/gold' },
  GGE:   { primaryAssetType: 'GGE',     navUrl: '/api/nav/gge'  },
  JAPAN: { primaryAssetType: 'JAPAN',   navUrl: '/api/japan/nav-latest?fund=JAPAN' },
  CHARTERIS: { primaryAssetType: 'CHARTERIS', navUrl: '/api/japan/nav-latest?fund=CHARTERIS' },
  AMATI: { primaryAssetType: 'AMATI', navUrl: '/api/japan/nav-latest?fund=AMATI' },
  MINERS: { primaryAssetType: 'MINERS', navUrl: '/api/japan/nav-latest?fund=MINERS' },
  JAPANESG: { primaryAssetType: 'JAPANESG', navUrl: '/api/japan/nav-latest?fund=JAPANESG' },
  TECH: { primaryAssetType: 'TECH', navUrl: '/api/japan/nav-latest?fund=TECH' },
  ELECTRUM: { primaryAssetType: 'ELECTRUM', navUrl: '/api/japan/nav-latest?fund=ELECTRUM' },
  JHTECH: { primaryAssetType: 'JHTECH', navUrl: '/api/japan/nav-latest?fund=JHTECH' },
  ASIATECH: { primaryAssetType: 'ASIATECH', navUrl: '/api/japan/nav-latest?fund=ASIATECH' },
  BARINGSOIL: { primaryAssetType: 'BARINGSOIL', navUrl: '/api/japan/nav-latest?fund=BARINGSOIL' },
  SPWORLD: { primaryAssetType: 'SPWORLD', navUrl: '/api/japan/nav-latest?fund=SPWORLD' },
  SMALLCAP: { primaryAssetType: 'SMALLCAP', navUrl: '/api/japan/nav-latest?fund=SMALLCAP' },
}

const { portfolio } = usePortfolio()

// Performance mini row (spec 2026-09-20-pnl-performance-charts-design.md
// section 3). JH follows the same access gate as the rest of the app; the
// grid collapses to the cells present so there is no hole.
const { isFundVisible, hasAccess, accessibleFunds } = useFundEnablement()
// Final-review fix I3 (Japan Phase 2, spec 2026-09-27 gate table): the
// per-fund list runs through the shared entitlement predicate —
// entitlements not loaded ⇒ gated funds hidden, live funds shown; loaded ⇒
// exact filter. FUNDS stays the static universe (fundState keys);
// visibleFunds drives the hero Σ, fetches, ledger rows and perf minis.
const visibleFunds = computed<readonly Fund[]>(() => FUNDS.filter(f => hasAccess(f)))
const PERF_FUNDS: readonly PerformanceFund[] = ['BGA', 'JH', 'GOLD', 'GGE', 'JAPAN', 'CHARTERIS', 'AMATI', 'MINERS', 'JAPANESG', 'TECH', 'ELECTRUM', 'JHTECH', 'ASIATECH', 'BARINGSOIL', 'SPWORLD', 'SMALLCAP', 'TOTAL']
// 2026-11-01: a fund the caller allocates nothing to (off, or weight 0) gets no mini. Null = not
// loaded or the fetch failed, so every accessible fund shows.
const fundsWithAllocation = ref<Set<string> | null>(null)
const perfFunds = computed(() => PERF_FUNDS.filter(f => f === 'TOTAL'
  || (hasAccess(f) && (fundsWithAllocation.value === null || fundsWithAllocation.value.has(f)))))

// Option C PR 3 (2026-10-11): the page's registry fund; a page whose fund has
// no P&L state here (the OTP overlay) shows BGA.
const displayFund = computed<Fund>(() => {
  const f = fundForPortfolio(portfolio.value)
  return (FUNDS as readonly string[]).includes(f) ? f as Fund : 'BGA'
})

// Per-fund state fetched in parallel each poll cycle. Enables the hero
// rollup (Σ across funds) and the Per-Fund Breakdown table to render off
// a single reactive object without duplicating fetch plumbing.
interface FundState {
  bs: BalanceSheetData | null
  pnl: DailyPnlData | null
  // T0 only: the daily P&L of the last struck NAV date (prev_official_nav_date) —
  // what the "Today" column shows before today's strike.
  prevPnl: DailyPnlData | null
  // T0 only: the balance sheet of that same date — the units held INTO today's
  // strike (today's sheet already carries units bought at it once booked).
  prevBs: BalanceSheetData | null
  enrichedNav: EnrichedSynthNavData | null
  officialNavDate: string | null
}
function emptyFundState(): FundState {
  return { bs: null, pnl: null, prevPnl: null, prevBs: null, enrichedNav: null, officialNavDate: null }
}
const fundState = reactive<Record<Fund, FundState>>({
  BGA:   emptyFundState(),
  JH:    emptyFundState(),
  GOLD:  emptyFundState(),
  GGE:   emptyFundState(),
  JAPAN: emptyFundState(),
  CHARTERIS: emptyFundState(),
  AMATI: emptyFundState(),
  MINERS: emptyFundState(),
  JAPANESG: emptyFundState(),
  TECH: emptyFundState(),
  ELECTRUM: emptyFundState(),
  JHTECH: emptyFundState(),
  ASIATECH: emptyFundState(),
  BARINGSOIL: emptyFundState(),
  SPWORLD: emptyFundState(),
  SMALLCAP: emptyFundState(),
})

// Funds whose page shows the "no position — you're in cash" card (GOLD, GGE; JAPAN since
// plan 2026-09-29 Task 15; CHARTERIS since Charteris Phase 3; AMATI since its Phase 3). Label from
// the registry ('GOLD', 'GGE', 'Japan', 'Silver', 'Copper').
const EMPTY_STATE_NOUN: Partial<Record<Fund, string>> =
  { GOLD: 'gold allocation', GGE: 'energy allocation', JAPAN: 'Japan holding', CHARTERIS: 'Charteris holding',
    AMATI: 'Amati holding', MINERS: 'Miners holding', JAPANESG: 'Japan ESG holding', TECH: 'Tech holding', ELECTRUM: 'Electrum holding',
    JHTECH: 'JHTech holding', ASIATECH: 'AsiaTech holding', BARINGSOIL: 'BaringsOil holding', SPWORLD: 'SPWorld holding', SMALLCAP: 'SmallCap holding' }
const displayLabel = computed(() =>
  REGISTERED_PORTFOLIOS.map(s => FUND_REGISTRY[s]).find(e => e.fund === displayFund.value)?.label ?? displayFund.value)
const showEmptyPosition = computed(() => {
  if (!EMPTY_STATE_NOUN[displayFund.value]) return false
  const positions = fundState[displayFund.value].bs?.positions
  return !positions || positions.length === 0
})
// pnl-summary rows for a gated fund only when the caller may see it (defence in depth: the
// server only returns a JAPAN row for a JAPAN holder, and only entitled users can hold it).
const summaryRows = computed(() => (pnlSummary.value?.byFund ?? []).filter(r =>
  (!(GATED_FUNDS as readonly string[]).includes(r.fund_id) || isFundVisible(r.fund_id))
  && isLiveFundAccessibleFor(accessibleFunds.value, r.fund_id)))

const pnlSummary = ref<{
  total: number
  byFund: Array<{ fund_id: string; pnl: number; periodFrom: string; periodTo: string }>
} | null>(null)

// "Today" column / stale-Synthetic fix (2026-09-06, paolo): gold's own
// `overnight_return_gbp` (on fundState.GOLD.enrichedNav) is T-1 aligned —
// see goldHoldingsRows.ts's computeGoldTodaysMovePct doc comment — so gold
// needs its live equivalent derived client-side from the 60s
// GoldHoldingsHybridService publisher (same source HoldingsPanel's live
// RET% fix, commit 1c8a2243, already reads). BGA/JH read overnight_return_gbp
// straight off the payload — their shared synth_nav.py engine recomputes it on
// every live tick.
const goldHoldingsLatest = ref<GoldHoldingsLatestResponse | null>(null)
const goldHybrid = computed(() => deriveHoldingsHybrid(goldHoldingsLatest.value))
// 2026-11-03 (paolo: "the first time I see figures across different pages that actually agree"): GGE's one live
// value is the shadow K_t, published on synth_nav:gge:live (the same key the Signal hero and the FOF K_t column read).
// The old client-side hybrid bridge is deleted.
const ggeLive = ref<GgeSynthNavLive | null>(null)
// 2026-11-04: BGA's/JH's shadow K_t live value (synth_nav:{bga|jh}:live) — present only while the fund's
// total_source flag = shadow; null ⇒ the engine's enriched payload, as before.
const bgaJhLive = reactive<Record<'BGA' | 'JH', BgaJhSynthNavLive | null>>({ BGA: null, JH: null })
function shadowLive(f: Fund): BgaJhSynthNavLive | null {
  if (f !== 'BGA' && f !== 'JH') return null
  const l = bgaJhLive[f]
  return l && l.tier !== 'critical' ? l : null
}

// The strike estimate of today's NAV vs the previous official NAV: BGA/JH on the shadow, the shadow's own
// prediction (anchor ÷ last official − 1) while today's NAV is an estimate; every other case the payload's snapshot.
function fundStrikeSnapshot(f: Fund): number | null {
  const l = shadowLive(f)
  if (l && l.anchorKind === 'estimate' && l.lastOfficialPence > 0) return l.anchorPence / l.lastOfficialPence - 1
  return fundState[f].enrichedNav?.snapshot_overnight_return_gbp ?? null
}

const loading = ref(true)
const subledgerAvailable = ref(true)
let timer: ReturnType<typeof setInterval> | null = null

// ── Trading day tabs ──
type PnlPeriod = 'T0' | 'T-1' | 'T-2' | 'T-3'
const validPeriods: PnlPeriod[] = ['T0', 'T-1', 'T-2', 'T-3']

function getPeriodFromHash(): PnlPeriod {
  const raw = window.location.hash.replace('#', '')
  const parts = raw.split('/')
  if (parts.length > 1) {
    const p = parts[1] as PnlPeriod
    if (validPeriods.includes(p)) return p
  }
  return 'T0'
}

const activePeriod = ref<PnlPeriod>(getPeriodFromHash())
const calendarData = ref<CalendarStatus | null>(null)

function getT0TradingDay(): string {
  if (calendarData.value?.tradingDay) return new Date().toISOString().slice(0, 10)
  if (calendarData.value?.nextTradingDay) return calendarData.value.nextTradingDay
  const d = new Date()
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1)
  return d.toISOString().slice(0, 10)
}

const nextTradingDay = computed(() => getT0TradingDay())
const pastTradingDays = computed(() => {
  if (calendarData.value?.pastTradingDays?.length) return calendarData.value.pastTradingDays
  const dates: string[] = []
  const d = new Date(getT0TradingDay() + 'T00:00:00')
  d.setDate(d.getDate() - 1)
  while (dates.length < 3) {
    const dow = d.getDay()
    if (dow !== 0 && dow !== 6) dates.push(d.toISOString().slice(0, 10))
    d.setDate(d.getDate() - 1)
  }
  return dates
})

const selectedDate = computed(() => {
  if (activePeriod.value === 'T0') return nextTradingDay.value
  const idx = { 'T-1': 0, 'T-2': 1, 'T-3': 2 }[activePeriod.value] ?? 0
  return pastTradingDays.value[idx]
})

const isForwardLooking = computed(() => activePeriod.value === 'T0')

// Date half of the tab label ("Tue 1 Sept"). Rendered in its own span so the
// ≤768px media rule can hide it — desktop shows "T0 (Tue 1 Sept)", mobile
// shows bare "T0". Same split as NavPanel (PR #187).
function periodDateSuffix(period: PnlPeriod): string {
  let date: string
  if (period === 'T0') date = nextTradingDay.value
  else {
    const idx = { 'T-1': 0, 'T-2': 1, 'T-3': 2 }[period] ?? 0
    date = pastTradingDays.value[idx]
  }
  if (!date) return ''
  const d = new Date(date + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

// ── Format helpers ──
// Default 0 decimals per paolo's 2026-08-09 note ("don't use decimals, too
// much space"). Cent-precision remains available via explicit decimals arg.
function fmtGbp(v: number | null | undefined, decimals = 0): string {
  if (v == null) return '--'
  const sign = v >= 0 ? '' : '-'
  const abs = Math.abs(v)
  const formatted = abs.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  return `${sign}£${formatted}`
}

function fmtUnits(v: number | null | undefined): string {
  if (v == null || v === 0) return '--'
  return v.toLocaleString('en-GB', { maximumFractionDigits: 0 })
}

function valClass(v: number | null | undefined): string {
  if (v == null || v === 0) return ''
  return v > 0 ? 'positive' : 'negative'
}

// ── Per-fund derived helpers ─────────────────────────────────
// Every helper takes a Fund and reads the reactive fundState.

// Spec 2026-10-06 (provisional deal overlay): today's executed deals reshape the
// position before every synth / Today / unrealised calculation below. Every
// balance-sheet fetch carries all funds' entries; prefer the fund's own fetch.
// Anchor sheet for the cross-fund figures (Portfolio Value, the position
// breakdown, the provisional fallback). Every fund's balance sheet carries the
// same household positions, NPV and provisional entries, so the first visible
// fund whose fetch succeeded serves. 2026-11-02: this used to be BGA's fetch —
// a user who restricts their own BGA (Paolo, 11-01) got a 403 there and lost
// the whole breakdown, Portfolio Value and every provisional deal of a fund
// whose own sheet 404s (MINERS on its first BUY day, no account_state row yet).
const anchorBs = computed<BalanceSheetData | null>(() => {
  for (const f of visibleFunds.value) {
    const bs = fundState[f].bs
    if (bs) return bs
  }
  return null
})

function provisionalEntries(f?: Fund) {
  return (f ? fundState[f].bs?.provisional : undefined) ?? anchorBs.value?.provisional ?? []
}

function fundBasePosition(f: Fund) {
  return fundState[f].bs?.positions?.find(p => p.assetType === FUND_CONFIG[f].primaryAssetType) ?? null
}

function fundOverlay(f: Fund) {
  return overlayPosition(fundBasePosition(f), provisionalEntries(f), FUND_CONFIG[f].primaryAssetType, fundSyntheticNav(f))
}

function fundPosition(f: Fund) {
  return fundOverlay(f).position
}

// Sold today, past the valuation point: the sold units' P&L is locked at the
// strike price (est. until the contract note books). Shown under Realised
// (today), not Synthetic (paolo 2026-10-20: a sold position must not read as
// still accruing) — Synthetic is only ever the units still held.
function fundSoldAtStrikePnl(f: Fund): number {
  const o = fundOverlay(f)
  const base = fundBasePosition(f)
  if (!o.soldUnits || o.soldPriceGbp == null || !base?.avgCost) return 0
  return (o.soldPriceGbp - base.avgCost) * o.soldUnits
}

// Tag for that locked sale P&L: 'booked' once the contract note prices it.
function fundSoldAtStrikeTag(f: Fund): string {
  if (!fundSoldAtStrikePnl(f)) return ''
  const sell = provisionalEntries(f).find(e =>
    e.assetType === FUND_CONFIG[f].primaryAssetType && e.side === 'SELL' && e.phase !== 'PreStrike')
  return sell?.phase === 'Booked' ? 'booked' : 'est.'
}

function fundHasOfficialNav(f: Fund): boolean {
  const date = fundState[f].officialNavDate
  if (!isForwardLooking.value) return date != null
  if (date == null) return false
  return date === new Date().toISOString().slice(0, 10)
}

// Gold's live-derived move (Arc B hybrid publisher). See
// goldHoldingsRows.ts's computeGoldTodaysMovePct for the full derivation —
// this replaces gold's stale enriched-payload overnight_return_gbp both
// here (Synthetic column fix) and in the new Today column below.
//
// 2026-09-21 (paolo P&L defect): when a SELL has been booked for today's
// strike (enrichedNav.sell_pending_at_strike), the aggregation switches to
// per-holding strikePrice on the hybrid rows — the frame frozen at 12:00 UK
// by GoldHoldingsHybridService — so the figure locks at the fund's
// valuation point instead of drifting with the afternoon US session and
// post-noon gold-futures moves. Position still held (no pending sell) →
// unchanged live behaviour, since a held position IS exposed to those
// post-noon moves (they belong to tomorrow's NAV).
const goldTodaysLiveMovePct = computed(() =>
  computeGoldTodaysMovePct(
    fundState.GOLD.enrichedNav, goldHybrid.value.rowsByTicker,
    { sellPending: fundState.GOLD.enrichedNav?.sell_pending_at_strike ?? false },
  )
)

// Today's live synth-move fraction for a fund. BGA/JH read the shadow K_t's
// move from synth_nav:{bga|jh}:live while their flag is shadow, else their
// enriched payload; GOLD substitutes the client-derived live figure (its
// payload's field is T-1 aligned); GGE reads the shadow K_t's move from
// synth_nav:gge:live.
function fundTodaysSynthMovePct(f: Fund): number | null {
  if (f === 'GOLD') return goldTodaysLiveMovePct.value
  if (f === 'GGE') return ggeLive.value?.liveReturnGbp ?? null
  const live = shadowLive(f)
  if (live) return live.liveReturnGbp
  return fundState[f].enrichedNav?.overnight_return_gbp ?? null
}

// ── Strike-day boundary (paolo 2026-10-09: "tomorrow becomes today at the
// strike" — 10:00 UK BGA, 12:00 the others) ──────────────────────────────
//   pre    — before today's strike: the coming NAV is still being estimated
//   post   — strike passed, today's NAV not yet published (estimate frozen)
//   landed — today's official NAV is in
// BGA/JH: their engine publishes the snapshot only after its strike. Every
// other fund's snapshot is labelled by the endpoint — kt_live before the 12:00
// strike is booked, strike_booked after (GOLD/GGE #767; CHARTERIS/AMATI
// 2026-10-16, which until then sat in 'pre' all afternoon: Today read the
// previous day and Tomorrow carried the struck move).
type StrikeState = 'pre' | 'post' | 'landed'

function fundStrikeState(f: Fund): StrikeState {
  if (fundHasOfficialNav(f)) return 'landed'
  const en = fundState[f].enrichedNav
  const today = new Date().toISOString().slice(0, 10)
  if (!en || en.snapshot_overnight_return_gbp == null || en.snapshot_strike_date !== today) return 'pre'
  if (en.snapshot_source != null) return en.snapshot_source === 'strike_booked' ? 'post' : 'pre'
  return f === 'BGA' || f === 'JH' ? 'post' : 'pre'
}

// Before the strike, the estimate of the coming NAV's move vs the last official
// NAV. GOLD/GGE: the K_t estimate alone once it is published for today — the
// live since-close move is NOT chained on top, so the morning window where K_t
// has published but the holdings payload hasn't rolled no longer counts last
// night's session twice. Before K_t publishes, the since-close move (payload
// not rolled yet) is the estimate. BGA/JH/JAPAN: the engine's live figure.
function fundPreStrikeMovePct(f: Fund): number | null {
  const en = fundState[f].enrichedNav
  if (f === 'GOLD' || f === 'GGE') {
    const today = new Date().toISOString().slice(0, 10)
    if (en?.snapshot_source === 'kt_live' && en.snapshot_strike_date === today && en.snapshot_overnight_return_gbp != null)
      return en.snapshot_overnight_return_gbp
  }
  return fundTodaysSynthMovePct(f)
}

function fundSyntheticNav(f: Fund): number | null {
  const en = fundState[f].enrichedNav
  if (!en || en.prev_official_nav_gbp == null) return null
  const prev = en.prev_official_nav_gbp
  if (prev <= 0) return null
  if ((f === 'GOLD' || f === 'GGE') && fundStrikeState(f) === 'pre') {
    const pre = fundPreStrikeMovePct(f)
    return pre == null ? null : prev * (1 + pre)
  }
  const movePct = fundTodaysSynthMovePct(f)
  if (movePct == null) return null
  const snapshot = fundStrikeSnapshot(f)
  const anchor = snapshot != null ? prev * (1 + snapshot) : prev
  return anchor * (1 + movePct)
}

// paolo's rule (2026-08-09): synth is the LIVE-TICKING estimate used only
// when the official NAV hasn't been booked for the day. Once official has
// landed, unrealised (from asset_positions.unrealised_pnl computed against
// the official mark) replaces synth — synth reads 0. Position closed
// (units=0) → both read 0.
//
// 2026-10-08 (paolo): GOLD/GGE value at 12:00 UK off the PREVIOUS night's US
// closes, so after today's NAV lands the move since that close is still
// accruing for TOMORROW's NAV. For those funds synth keeps showing it on top
// of the official mark — official × live move × units — while Unrealised
// carries the gain against cost at the official mark, so the two never
// overlap. The live move is measured from last night's close, exactly where
// today's NAV stops.
//
// BGA/JH joined later the same day: their live move (overnight_return_gbp) runs
// from the frozen 10:00 / 12:00 strike, and official × that move is the better
// predictor of tomorrow's NAV — BGA 89d: RMSE 68.6bp, slope 0.95 (vs 98.5bp for
// chaining the strike estimate); JH 130d: 88.1bp, slope 1.01 (vs 101.8bp
// measured from the US close). Both beat zero (139.5 / 160.8bp).
//
// CHARTERIS and AMATI joined 2026-10-15 (paolo): both value at 12:00 UK, so the
// TSX/US session after that is tomorrow's NAV, as for GOLD. Each live publisher
// re-anchors on the landed NAV, so overnight_return_gbp is the move since it.
//
// JAPAN joined 2026-10-27 (paolo): it was left out because it "prices same-day
// Tokyo closes", but that is why it belongs here — today's NAV holds today's
// Tokyo close, so NIY after it is tomorrow's NAV, and the live publisher
// re-anchors on the landed NAV like the others (row read £0 vs a +1.6% synth).
// Pack funds joined 2026-11-02 (paolo: MINERS showed no synthetic for the amount committed today): each
// values at 12:00 UK (JAPANESG at the Tokyo close) and FundPackLiveSynthPublisherService re-anchors on the
// landed NAV exactly like the CHARTERIS/AMATI publishers, so the same post-NAV rule applies.
const POST_NAV_SYNTH_FUNDS: readonly Fund[] = ['BGA', 'JH', 'GOLD', 'GGE', 'JAPAN', 'CHARTERIS', 'AMATI',
  'MINERS', 'JAPANESG', 'TECH', 'ELECTRUM', 'JHTECH', 'ASIATECH', 'BARINGSOIL', 'SPWORLD', 'SMALLCAP']

function fundPostNavSynthetic(f: Fund, units: number): number | null {
  if (!POST_NAV_SYNTH_FUNDS.includes(f)) return null
  const official = fundState[f].enrichedNav?.today_official_nav_gbp
  const movePct = fundTodaysSynthMovePct(f)
  if (official == null || official <= 0 || movePct == null) return null
  return official * movePct * units
}

function fundSynthetic(f: Fund): number {
  const p = fundPosition(f)
  if (!p || !p.units) return 0
  if (fundHasOfficialNav(f)) return isForwardLooking.value ? fundPostNavSynthetic(f, p.units) ?? 0 : 0
  const nav = fundSyntheticNav(f)
  if (nav == null || !p.avgCost) return 0
  return (nav - p.avgCost) * p.units
}

function fundUnrealised(f: Fund): number {
  if (!fundHasOfficialNav(f)) return 0
  return fundPosition(f)?.unrealisedPnl ?? 0
}

function fundRealisedToday(f: Fund): number {
  return (fundState[f].pnl?.realisedPnl ?? 0) + fundSoldAtStrikePnl(f)
}

function fundCumulativeRealised(f: Fund): number {
  return fundState[f].pnl?.cumulativeRealisedPnl ?? 0
}

// Convention B — closed-trade P&L (paolo 2026-08-23 companion).
// Distinct from cumulativeRealisedPnl above which uses yesterday's-close
// basis and captures interim MTM in a separate bucket. This uses each
// SELL's original FIFO buy-lot cost. Both totals converge on the same
// final P&L; the split differs across days.
function fundCumulativeClosedTrade(f: Fund): number {
  return fundState[f].pnl?.cumulativeClosedTradePnl ?? 0
}

function fundUnits(f: Fund): number {
  return fundPosition(f)?.units ?? 0
}

// "Today" / "Tomorrow" columns (paolo 2026-10-09; replaces the single 2026-09-06
// "Today" column). The day turns at the fund's strike: what has built up for
// the coming NAV ("Tomorrow") becomes "Today" when that NAV is struck, and a new
// "Tomorrow" starts. All prices are POUNDS per unit (BalanceSheetData), so value
// = nav × units with no pence conversion.
//
// Today — the move of the last struck NAV on the units held INTO that strike
// (units sold at the strike included, units bought at it excluded — they were
// bought at that NAV):
//   pre    → the previous NAV date's subledger P&L (MTM + realised), exact
//   post   → overnight units × prev NAV × the frozen strike estimate
//   landed → today's subledger P&L (MTM on held + realised on sold), exact;
//            overnight units × (official − prev) until that row is written
// The subledger books realised on a yesterday's-close basis, so MTM + realised
// is exactly the struck move (checked: GOLD 06 Oct sell −£1,031.56 =
// 97,317u × (113.86 − 114.92)p; JH 06 Oct buy MTM £0.41).
function dailyStruckMove(p: DailyPnlData | null): number {
  return (p?.bgaMtmPnl ?? 0) + (p?.realisedPnl ?? 0)
}

// Units held into today's strike = the previous NAV date's closing position.
// Never today's: once the contract note books, today's position includes units
// bought AT the strike, and (official − prev) on those is a phantom loss/gain
// (2026-10-15: GOLD −£3,505 / CHARTERIS −£4,189 on a fresh buy). Falls back to
// today's position only when the previous sheet could not be fetched.
function fundOvernightUnits(f: Fund): number {
  const prevBs = fundState[f].prevBs
  if (prevBs) return prevBs.positions?.find(p => p.assetType === FUND_CONFIG[f].primaryAssetType)?.units ?? 0
  return fundBasePosition(f)?.units ?? 0
}

function fundTodayGbp(f: Fund): number {
  if (!isForwardLooking.value) return dailyStruckMove(fundState[f].pnl)
  const en = fundState[f].enrichedNav
  const prev = en?.prev_official_nav_gbp ?? null
  const overnightUnits = fundOvernightUnits(f)
  switch (fundStrikeState(f)) {
    case 'pre':
      return dailyStruckMove(fundState[f].prevPnl)
    case 'post': {
      const snap = fundStrikeSnapshot(f)
      return prev != null && prev > 0 && snap != null ? overnightUnits * prev * snap : 0
    }
    case 'landed': {
      const booked = dailyStruckMove(fundState[f].pnl)
      if (booked !== 0) return booked
      const official = en?.today_official_nav_gbp
      return prev != null && prev > 0 && official != null && official > 0 ? overnightUnits * (official - prev) : 0
    }
  }
}

// Tomorrow — what is building for the coming NAV, on the units held now:
//   pre    → units × prev NAV × the estimate of the NAV about to be struck
//   post   → units × strike estimate × live move (BGA/JH since the strike;
//            GOLD/GGE since last night's US close — 10-09 test: the noon reset
//            predicts worse for them, 200 vs 176bp gold, 80 vs 72bp GGE)
//   landed → units × official × live move (fundPostNavSynthetic, #768/#769)
// Units bought at today's strike join here (at the strike price); units sold
// at it drop out.
function fundTomorrowGbp(f: Fund): number {
  if (!isForwardLooking.value) return 0
  const units = fundPosition(f)?.units ?? 0
  if (!units) return 0
  const en = fundState[f].enrichedNav
  const prev = en?.prev_official_nav_gbp ?? null
  const state = fundStrikeState(f)
  if (state === 'landed') return fundPostNavSynthetic(f, units) ?? 0
  if (prev == null || prev <= 0) return 0
  if (state === 'pre') {
    const m = fundPreStrikeMovePct(f)
    return m == null ? 0 : units * prev * m
  }
  const m = fundTodaysSynthMovePct(f)
  const snap = fundStrikeSnapshot(f) ?? 0
  return m == null ? 0 : units * prev * (1 + snap) * m
}

function fundRowTotal(f: Fund): number {
  return fundSynthetic(f) + fundUnrealised(f) + fundRealisedToday(f)
}

// ── Aggregate hero values (Σ across funds) ────────────────────
// Portfolio Value: cross-fund total from any fund's balance-sheet — the
// GetBalanceSheetAsync endpoint recomputes liveNpv across ALL assets so
// the value is invariant to fundId. We use the anchor sheet (first visible
// fund that loaded) as the primary.
const liveNavByFund = computed<Record<string, number | null>>(() =>
  Object.fromEntries(visibleFunds.value.map(f => [f, fundSyntheticNav(f)])))
// Spec 2026-10-06: priced provisional deals (sale proceeds at the strike,
// est. units bought) adjust the cross-fund total until the reconcile lands.
const portfolioAdjustment = computed(() =>
  portfolioValueAdjustment(anchorBs.value?.positions ?? [], provisionalEntries(), liveNavByFund.value))
const portfolioValue = computed(() => {
  const v = anchorBs.value?.account?.netPortfolioValue
  return v == null ? null : v + portfolioAdjustment.value
})
const provisionalLineItems = computed(() =>
  buildProvisionalLines(provisionalEntries(), liveNavByFund.value, new Date()))
const breakdownPositions = computed(() => {
  const all = anchorBs.value?.positions ?? []
  const entries = provisionalEntries()
  const assets = [...new Set([...all.map(p => p.assetType), ...entries.map(e => e.assetType)])]
  return assets.flatMap(a => {
    const base = all.find(p => p.assetType === a) ?? null
    if (base && !entries.some(e => e.assetType === a)) return [base]   // no deal today: unchanged
    const fund = visibleFunds.value.find(f => FUND_CONFIG[f].primaryAssetType === a)
    const pos = overlayPosition(base, entries, a, fund ? fundSyntheticNav(fund) : null).position
    return pos && pos.units > 0 ? [pos] : []
  })
})

const heroSynthetic       = computed(() => visibleFunds.value.reduce((s, f) => s + fundSynthetic(f), 0))
const heroToday           = computed(() => visibleFunds.value.reduce((s, f) => s + fundTodayGbp(f), 0))
const heroTomorrow        = computed(() => visibleFunds.value.reduce((s, f) => s + fundTomorrowGbp(f), 0))
const heroUnrealised      = computed(() => visibleFunds.value.reduce((s, f) => s + fundUnrealised(f), 0))
const heroFloating        = computed(() => heroSynthetic.value + heroUnrealised.value)
const heroRealisedToday   = computed(() => visibleFunds.value.reduce((s, f) => s + fundRealisedToday(f), 0))
const heroCumRealised     = computed(() => visibleFunds.value.reduce((s, f) => s + fundCumulativeRealised(f), 0))
const heroCumClosedTrade  = computed(() => visibleFunds.value.reduce((s, f) => s + fundCumulativeClosedTrade(f), 0))
const heroRowTotal        = computed(() => heroFloating.value + heroRealisedToday.value)

// Single-fund P&L (used by P&L Attribution ledger below)
const displayPnl = computed(() => fundState[displayFund.value].pnl)

// P&L Attribution single-fund labels
const mtmLabel = computed(() => `${displayFund.value} MTM P&L`)
const equityComponent = computed(() => {
  const p = displayPnl.value
  if (p == null) return null
  return p.bgaMtmPnl - (p.fxPnl ?? 0)
})

// Fold synth/unrealised into P&L Summary — All Time by fund_id
function synthForFundId(fundId: string): number {
  if ((visibleFunds.value as readonly string[]).includes(fundId)) return fundSynthetic(fundId as Fund)
  return 0  // OTP has no per-fund synthetic concept (overlay assets)
}
function unrealisedForFundId(fundId: string): number {
  if ((visibleFunds.value as readonly string[]).includes(fundId)) return fundUnrealised(fundId as Fund)
  return 0
}

// ── Data fetching ──
async function fetchFund(f: Fund, date: string, forward: boolean): Promise<FundState> {
  const [bsR, pnlR, enR] = await Promise.allSettled([
    api.userBalanceSheet(date, f),
    api.userDailyPnl(date, f),
    forward ? api.enrichedSynthNav(f) : Promise.resolve(null),
  ])

  const bs = bsR.status === 'fulfilled' ? bsR.value : null
  const pnl = pnlR.status === 'fulfilled' ? pnlR.value : null
  const enRaw = enR.status === 'fulfilled' ? enR.value : null
  const enrichedNav = (enRaw && enRaw.available !== false) ? enRaw : null

  let officialNavDate: string | null = null
  try {
    const navRes = await fetch(FUND_CONFIG[f].navUrl, { credentials: 'include' })
    if (navRes.ok) {
      const nd = await navRes.json()
      officialNavDate = nd && nd.available !== false ? (nd.date ?? null) : null
    }
  } catch { /* ignore */ }

  let prevPnl: DailyPnlData | null = null
  let prevBs: BalanceSheetData | null = null
  const prevDate = enrichedNav?.prev_official_nav_date
  if (forward && prevDate) {
    const [ppR, pbR] = await Promise.allSettled([api.userDailyPnl(prevDate, f), api.userBalanceSheet(prevDate, f)])
    if (ppR.status === 'fulfilled') prevPnl = ppR.value   // else Today column falls back to £0
    if (pbR.status === 'fulfilled') prevBs = pbR.value
  }

  return { bs, pnl, prevPnl, prevBs, enrichedNav, officialNavDate }
}

async function fetchData() {
  try {
    const date = selectedDate.value
    const forward = isForwardLooking.value

    // Fetch all three funds in parallel.
    const fetchFunds = visibleFunds.value
    const results = await Promise.all(fetchFunds.map(f => fetchFund(f, date, forward)))
    fetchFunds.forEach((f, i) => { fundState[f] = results[i] })

    // Subledger availability: available when ANY visible fund's balance sheet loaded. It used to probe BGA only,
    // but BGA can be restricted (Target Pot v2, 2026-11-01) and is then never fetched, so every page read
    // "not initialised" while GGE etc. loaded fine.
    subledgerAvailable.value = fetchFunds.some(f => fundState[f].bs !== null)

    // Cross-fund P&L summary — pnlSummary.byFund gives the multi-fund
    // breakdown incl. OTP (see /subledger/pnl-summary endpoint).
    try {
      pnlSummary.value = await api.pnlSummary('BGA')
    } catch { pnlSummary.value = null }

    // Gold's live-move bridge (Today column / Synthetic staleness fix) —
    // fetched every cycle regardless of which fund is the display fund,
    // same as pnlSummary above, since the hero/per-fund breakdown always
    // includes GOLD. Best-effort: the publisher is inactive outside the US
    // session, and computeGoldTodaysMovePct degrades to null (falling back
    // to the stale per-holding return_pct_gbp) when it has nothing to offer.
    try {
      goldHoldingsLatest.value = await api.goldHoldingsLatest()
    } catch { goldHoldingsLatest.value = null }

    // GGE's one live value (the shadow K_t), fetched every cycle like gold's bridge.
    try {
      ggeLive.value = await api.ggeSynthNavLive()
    } catch { ggeLive.value = null }
    for (const f of ['BGA', 'JH'] as const) {
      try { bgaJhLive[f] = await api.bgaJhSynthNavLive(f) } catch { bgaJhLive[f] = null }
    }

    // Fetched every cycle (not once) so a superuser's view-as switch or a Strategy save shows up.
    try {
      fundsWithAllocation.value = allocatedFunds(await api.getFundAllocations())
    } catch { fundsWithAllocation.value = null }
  } catch {
    // Silently handle
  } finally {
    loading.value = false
  }
}

watch(activePeriod, () => {
  window.location.hash = `pnl/${activePeriod.value}`
  fetchData()
})

// Plan 2026-09-29 Task 15: re-fetch when the display fund or the visible-fund list changes
// (e.g. /api/auth/me lands and JAPAN becomes visible) instead of waiting for the 30 s poll.
watch(() => `${displayFund.value}|${visibleFunds.value.join(',')}`, () => { void fetchData() })

function onHashChange() {
  const p = getPeriodFromHash()
  if (p !== activePeriod.value) activePeriod.value = p
}

// PR ζ+1 (2026-08-21): gate-status badge, replacing the trust indicator
// that used to live on the now-deleted ReconciledPanel. Reads
// hl_reconciliation_gate for the current user via
// GET /api/user/reconciled/gate. GREEN = reconstructor tracks HL within
// 0.1%; AMBER = within 3.5% (broker-side publishing lag territory);
// RED = outside 3.5% (halt trust). PENDING = no evaluation yet.
type GateStatus = 'GREEN' | 'AMBER' | 'RED' | 'PENDING'
interface GateRow { status: GateStatus; deltaGbp: number; lastCheckedAt: string | null }
const gate = ref<GateRow | null>(null)
const gateBadgeClass = computed(() => {
  const s = gate.value?.status ?? 'PENDING'
  return `gate-badge gate-${s.toLowerCase()}`
})
const gateBadgeTooltip = computed(() => {
  const g = gate.value
  if (!g) return 'Reconstructor vs HL: awaiting first evaluation'
  const delta = g.deltaGbp >= 0 ? `+£${g.deltaGbp.toFixed(0)}` : `−£${Math.abs(g.deltaGbp).toFixed(0)}`
  const when = g.lastCheckedAt ? new Date(g.lastCheckedAt).toLocaleString('en-GB', { timeZone: 'Europe/London' }) : '—'
  return `Reconciler vs HL: delta ${delta} (last checked ${when})`
})
async function loadGate(): Promise<void> {
  try { gate.value = await api.reconciledGate() } catch { /* keep last known */ }
}
let gateTimer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  window.addEventListener('hashchange', onHashChange)
  api.calendarToday().then(c => { calendarData.value = c }).catch(() => {})
  fetchData()
  void loadGate()
  timer = setInterval(fetchData, 30000)
  gateTimer = setInterval(loadGate, 60_000)
})

onUnmounted(() => {
  window.removeEventListener('hashchange', onHashChange)
  if (timer) clearInterval(timer)
  if (gateTimer) clearInterval(gateTimer)
})
</script>

<template>
  <div class="pnl-panel">
    <!-- Period selector + reconciler-gate trust indicator (moved here from
         the deleted ReconciledPanel in PR ζ). -->
    <div class="pnl-header-row">
      <nav class="period-tabs">
        <button v-for="p in (['T0', 'T-1', 'T-2', 'T-3'] as PnlPeriod[])" :key="p"
          :class="['period-tab', { active: activePeriod === p }]"
          @click="activePeriod = p">
          <span class="period-tab-code">{{ p }}</span>
          <span class="period-tab-date" v-if="periodDateSuffix(p)"> ({{ periodDateSuffix(p) }})</span>
        </button>
      </nav>
      <span v-if="gate" :class="gateBadgeClass" :title="gateBadgeTooltip"
            data-testid="pnl-gate-badge">
        RECONCILER {{ gate.status }}
      </span>
    </div>

    <!-- Booked performance minis: 1M sparkline per fund + Total, click → full chart. -->
    <div class="pnl-perf-row" data-testid="pnl-perf-row">
      <PnlPerformanceMini v-for="f in perfFunds" :key="f" :fund="f" />
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading">Loading P&amp;L data...</div>

    <!-- Subledger not initialised -->
    <div v-else-if="!subledgerAvailable" class="unavailable-box">
      Subledger not initialised
    </div>

    <!-- Empty position card — "you're in cash" state for the single-class
         funds (GOLD; GGE at its Phase-4 launch; JAPAN, plan 2026-09-29 Task 15). -->
    <div v-else-if="showEmptyPosition"
         class="empty-position-card"
         data-testid="pnl-empty-position">
      <div class="empty-title">No {{ displayLabel }} position</div>
      <div class="empty-body">
        Cash-only. Your {{ EMPTY_STATE_NOUN[displayFund] }} will settle here
        when the next deal executes at Hargreaves.
      </div>
    </div>

    <template v-else>
      <!-- ═══════════════════════════════════════════════ -->
      <!-- Hero: Portfolio Value + rolled-up Unrealised + Realised -->
      <!-- ═══════════════════════════════════════════════ -->

      <div class="hero-grid-3col">
        <!-- Portfolio value (cross-fund total) -->
        <div class="hero-card">
          <div class="hero-label">Portfolio Value</div>
          <div class="hero-value">{{ fmtGbp(portfolioValue) }}</div>
        </div>

        <!-- Unrealised P&L (Σ synthetic + Σ unrealised across funds) -->
        <div class="hero-card" :class="heroFloating >= 0 ? 'border-green' : 'border-red'">
          <div class="hero-label">Unrealised P&amp;L</div>
          <div class="hero-value" :class="valClass(heroFloating)">{{ fmtGbp(heroFloating) }}</div>
          <div class="hero-sub">
            synth {{ fmtGbp(heroSynthetic) }} · unrl {{ fmtGbp(heroUnrealised) }} · today {{ fmtGbp(heroToday) }} · tomorrow {{ fmtGbp(heroTomorrow) }}
          </div>
        </div>

        <!-- Realised P&L today (Σ across funds) -->
        <div class="hero-card">
          <div class="hero-label">Realised P&amp;L (today)</div>
          <div class="hero-value" :class="valClass(heroRealisedToday)">{{ fmtGbp(heroRealisedToday) }}</div>
          <!-- Two cumulative views (paolo companion 2026-08-23):
               • accounting = ledger MTM-then-realise attribution — the
                 same number the daily P&L series uses (Sharpe / drawdown).
               • round-trip = closed-trade FIFO original-cost basis —
                 the "how much did closed trades net" reading.
               Both converge to the same final P&L; attribution differs. -->
          <div class="hero-sub">
            cum {{ fmtGbp(heroCumRealised) }} <span class="hero-sub-tag">accounting</span>
            · {{ fmtGbp(heroCumClosedTrade) }} <span class="hero-sub-tag">round-trip</span>
          </div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════ -->
      <!-- Per-Fund Breakdown                              -->
      <!-- ═══════════════════════════════════════════════ -->
      <div class="ledger-bare per-fund-section" data-testid="per-fund-breakdown">
        <div class="ledger-header-line">Per-Fund P&amp;L</div>
        <table class="ledger-flat">
          <thead>
            <tr>
              <th class="col-fund">Fund</th>
              <th class="col-num">Units</th>
              <th class="col-num">Synthetic</th>
              <th class="col-num" title="The last struck NAV's move on the units held into that strike (estimate until the official NAV lands). Rolls over at the strike: 10:00 UK BGA, 12:00 the others.">Today</th>
              <th class="col-num" title="What is building for the next NAV on the units held now. Becomes Today at the next strike.">Tomorrow</th>
              <th class="col-num">Unrealised</th>
              <th class="col-num">Realised (today)</th>
              <th class="col-num">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in visibleFunds" :key="f" class="ledger-row" :data-fund="f">
              <td class="col-fund">{{ f }}</td>
              <td class="col-num mono">{{ fmtUnits(fundUnits(f)) }}</td>
              <td class="col-num mono" :class="valClass(fundSynthetic(f))">{{ fmtGbp(fundSynthetic(f)) }}</td>
              <td class="col-num mono" :class="valClass(fundTodayGbp(f))" data-testid="pnl-today-cell">{{ fmtGbp(fundTodayGbp(f)) }}</td>
              <td class="col-num mono" :class="valClass(fundTomorrowGbp(f))" data-testid="pnl-tomorrow-cell">{{ fmtGbp(fundTomorrowGbp(f)) }}</td>
              <td class="col-num mono" :class="valClass(fundUnrealised(f))">{{ fmtGbp(fundUnrealised(f)) }}</td>
              <td class="col-num mono" :class="valClass(fundRealisedToday(f))">{{ fmtGbp(fundRealisedToday(f)) }}<span v-if="fundSoldAtStrikeTag(f)" class="hero-sub-tag"> {{ fundSoldAtStrikeTag(f) }}</span></td>
              <td class="col-num mono row-total" :class="valClass(fundRowTotal(f))">{{ fmtGbp(fundRowTotal(f)) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="ledger-total">
              <td class="col-fund" style="font-weight:600;">Total</td>
              <td></td>
              <td class="col-num mono" :class="valClass(heroSynthetic)" style="font-weight:600;">{{ fmtGbp(heroSynthetic) }}</td>
              <td class="col-num mono" :class="valClass(heroToday)" style="font-weight:600;">{{ fmtGbp(heroToday) }}</td>
              <td class="col-num mono" :class="valClass(heroTomorrow)" style="font-weight:600;">{{ fmtGbp(heroTomorrow) }}</td>
              <td class="col-num mono" :class="valClass(heroUnrealised)" style="font-weight:600;">{{ fmtGbp(heroUnrealised) }}</td>
              <td class="col-num mono" :class="valClass(heroRealisedToday)" style="font-weight:600;">{{ fmtGbp(heroRealisedToday) }}</td>
              <td class="col-num mono row-total" :class="valClass(heroRowTotal)" style="font-weight:700;">{{ fmtGbp(heroRowTotal) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- ═══════════════════════════════════════════════ -->
      <!-- P&L Summary — All Time (extended: + Synth + Unrl) -->
      <!-- ═══════════════════════════════════════════════ -->
      <div v-if="pnlSummary" class="ledger-bare pnl-summary-section">
        <div class="ledger-header-line">P&amp;L Summary — All Time</div>
        <table class="ledger-flat">
          <thead>
            <tr>
              <th class="col-fund">Fund</th>
              <th class="col-num">Synthetic</th>
              <th class="col-num">Unrealised</th>
              <th class="col-num">Cumulative Net</th>
              <th class="col-num">From</th>
              <th class="col-num">To</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in summaryRows" :key="row.fund_id" class="ledger-row">
              <td class="col-fund">{{ row.fund_id }}</td>
              <td class="col-num mono" :class="valClass(synthForFundId(row.fund_id))">{{ fmtGbp(synthForFundId(row.fund_id)) }}</td>
              <td class="col-num mono" :class="valClass(unrealisedForFundId(row.fund_id))">{{ fmtGbp(unrealisedForFundId(row.fund_id)) }}</td>
              <td class="col-num mono" :class="valClass(row.pnl)">{{ fmtGbp(row.pnl) }}</td>
              <td class="col-num mono date-cell">{{ row.periodFrom }}</td>
              <td class="col-num mono date-cell">{{ row.periodTo }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="ledger-total">
              <td class="col-fund" style="font-weight:600;">Total</td>
              <td class="col-num mono" :class="valClass(heroSynthetic)" style="font-weight:600;">{{ fmtGbp(heroSynthetic) }}</td>
              <td class="col-num mono" :class="valClass(heroUnrealised)" style="font-weight:600;">{{ fmtGbp(heroUnrealised) }}</td>
              <td class="col-num mono" :class="valClass(pnlSummary.total)" style="font-weight:700;">{{ fmtGbp(pnlSummary.total) }}</td>
              <td></td><td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Multi-asset position breakdown -->
      <PositionBreakdown
        v-if="anchorBs && (breakdownPositions.length > 0 || provisionalLineItems.length > 0)"
        :positions="breakdownPositions"
        :account="anchorBs.account"
        :provisional-lines="provisionalLineItems"
        :portfolio-adjustment-gbp="portfolioAdjustment"
      />

      <!-- P&L Attribution (single-fund view for displayFund) -->
      <div class="ledger-bare">
        <div class="ledger-header-line">{{ displayFund }} P&amp;L Attribution (today)</div>
        <table class="ledger-flat">
          <tbody>
            <tr class="ledger-row">
              <td class="ledger-label" data-testid="pnl-attr-mtm-label">{{ mtmLabel }}</td>
              <td class="ledger-val mono" :class="valClass(displayPnl?.bgaMtmPnl)">{{ fmtGbp(displayPnl?.bgaMtmPnl) }}</td>
            </tr>
            <tr class="ledger-row ledger-sub">
              <td class="ledger-label" style="padding-left:12px;color:#555;font-size:11px;">Equity component</td>
              <td class="ledger-val mono" :class="valClass(equityComponent)" style="font-size:11px;">{{ fmtGbp(equityComponent) }}</td>
            </tr>
            <tr class="ledger-row ledger-sub">
              <td class="ledger-label" style="padding-left:12px;color:#555;font-size:11px;">FX component (GBP/USD)</td>
              <td class="ledger-val mono" :class="valClass(displayPnl?.fxPnl)" style="font-size:11px;">{{ fmtGbp(displayPnl?.fxPnl) }}</td>
            </tr>
            <tr class="ledger-row">
              <td class="ledger-label">Realised P&amp;L</td>
              <td class="ledger-val mono" :class="valClass(displayPnl?.realisedPnl)">{{ fmtGbp(displayPnl?.realisedPnl) }}</td>
            </tr>
            <tr class="ledger-row">
              <td class="ledger-label">Costs</td>
              <td class="ledger-val mono" :class="valClass(displayPnl?.costs)">{{ fmtGbp(displayPnl?.costs) }}</td>
            </tr>
            <tr class="ledger-total">
              <td class="ledger-label" style="color:#aaa;font-weight:600;">Net P&amp;L (today)</td>
              <td class="ledger-val mono" :class="valClass(displayPnl?.netPnl)" style="font-weight:600;">{{ fmtGbp(displayPnl?.netPnl) }}</td>
            </tr>
            <tr class="ledger-row">
              <td class="ledger-label">Cumulative P&amp;L</td>
              <td class="ledger-val mono" :class="valClass(displayPnl?.cumulativePnl)">{{ fmtGbp(displayPnl?.cumulativePnl) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

    </template>
  </div>
</template>

<style scoped>
.pnl-header-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem;
  border-bottom: 2px solid #333;
  margin-bottom: 0.75rem;
}
.pnl-header-row .period-tabs { border-bottom: 0; margin-bottom: 0; }
.period-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #333;
  margin-bottom: 0.75rem;
}
.gate-badge {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 3px;
  margin-bottom: 4px;
  cursor: help;
  white-space: nowrap;
}
.gate-badge.gate-green   { background: #0f3a1f; color: #6fd68a; }
.gate-badge.gate-amber   { background: #4a3200; color: #ffb84d; }
.gate-badge.gate-red     { background: #4a0f0f; color: #ff6b6b; }
.gate-badge.gate-pending { background: #202020; color: #777; }
.period-tab {
  padding: 0.5rem 0.75rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: #888;
  font-size: 0.8rem;
  cursor: pointer;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.period-tab:hover { color: #ccc; }
.period-tab.active {
  color: #eee;
  border-bottom-color: #4caf50;
}

.pnl-panel {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.loading {
  text-align: center;
  color: #888;
  font-style: italic;
  padding: 2rem 0;
}

.unavailable-box {
  padding: 1.5rem 1rem;
  text-align: center;
  color: #ff9800;
  border: 1px solid #3a2a00;
  border-radius: 4px;
  background: #1a1400;
}

.empty-position-card {
  padding: 1.5rem 1rem;
  text-align: center;
  color: #666;
  border: 1px solid #333;
  border-radius: 4px;
  background: #1a1a1a;
}

.empty-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #999;
  margin-bottom: 0.4rem;
}

.empty-body {
  font-size: 0.8rem;
  color: #666;
  line-height: 1.4;
}

.section-header {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #666;
  margin-bottom: 0.5rem;
}

/* ── Hero (3-col: Portfolio / Unrealised / Realised) ──────── */

.hero-grid-3col {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
}

.hero-card {
  background: linear-gradient(135deg, #0d1a0d, #0a0a0a);
  border: 1px solid #1a3a1a;
  border-radius: 6px;
  padding: 0.75rem;
}

.hero-card.border-green { border-color: #1b5e20; }
.hero-card.border-red   { border-color: #7f1d1d; }

.hero-label {
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: #666;
  margin-bottom: 0.35rem;
}

.hero-value {
  font-family: monospace;
  font-size: 1.4rem;
  font-weight: 300;
  color: #eee;
  line-height: 1.2;
}

.hero-sub {
  font-family: monospace;
  font-size: 0.65rem;
  color: #888;
  margin-top: 0.35rem;
}

.hero-sub-tag {
  font-size: 0.55rem;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ── Bare ledger tables ────────────────────────────────── */

.ledger-bare {
  margin-bottom: 12px;
}

/* Eight columns since Today/Tomorrow (2026-10-09): on a phone the per-fund
   table scrolls inside its own box instead of widening the page. */
.per-fund-section {
  overflow-x: auto;
}

.ledger-header-line {
  font-size: 9px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 2px;
  padding: 4px 0;
  border-bottom: 1px solid #222;
}

.ledger-flat {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.ledger-flat thead th {
  text-align: right;
  font-weight: 500;
  font-size: 10px;
  padding: 4px;
  color: #666;
  border-bottom: 1px solid #222;
}

.ledger-flat th.col-fund {
  text-align: left;
}

.ledger-row td {
  padding: 5px 4px;
  border-bottom: 1px solid #1a1a1a;
}

.ledger-sub td {
  padding-top: 2px;
  padding-bottom: 2px;
  border-bottom: 1px solid #1a1a1a;
}

.col-fund { color: #ccc; text-align: left; }
.col-num  { text-align: right; color: #ccc; }
.date-cell { color: #666; font-size: 10px; }
.row-total { border-left: 1px solid #333; padding-left: 8px; }

.ledger-label { color: #888; }
.ledger-val { text-align: right; color: #ccc; }
.mono { font-family: monospace; }

.ledger-total td {
  border-top: 1px solid #333;
  border-bottom: none;
  padding: 5px 4px;
}

/* ── Value colouring ───────────────────────────────────── */

.positive { color: #4caf50 !important; }
.negative { color: #f44336 !important; }

/* ── Mobile responsive ─────────────────────────────────── */

@media (max-width: 768px) {
  .hero-grid-3col {
    grid-template-columns: 1fr;
  }

  .hero-value {
    font-size: 1.4rem;
  }

  .hero-label {
    font-size: 0.75rem;
  }

  .section-header {
    font-size: 0.7rem;
  }

  .col-fund, .col-num { font-size: 12px; }
  .ledger-label, .ledger-val { font-size: 12px; }
  .date-cell { font-size: 11px; }

  /* Mobile: bare T-code only ("T0"); the "(Tue 1 Sept)" suffix would make
     the four tabs wrap against the reconciler badge. */
  .period-tab { padding: 0.5rem 0.6rem; min-height: 44px; }
  .period-tab-date { display: none; }
}

.pnl-perf-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 8px; margin-bottom: 10px; }
@media (max-width: 768px) { .pnl-perf-row { grid-template-columns: repeat(2, 1fr); } }
</style>
